import { useState, useEffect, useCallback } from 'react';
import { getInstructorReviewsApi, replyToReviewApi, deleteReviewApi } from '../../../../api/apiServices';
import { useAuth } from '../../../../context/useAuth';

export default function InstructorReviewsManager() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    repliedCount: 0,
    unrepliedCount: 0,
    averageRating: 4.8,
    fiveStarRatio: 100
  });
  const [loading, setLoading] = useState(true);

  // Filters
  const [filter, setFilter] = useState('all'); // 'all' | 'unreplied' | 'replied' | '5star' | 'critical'
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Reply drawer / modal state
  const [replyingReview, setReplyingReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionError, setActionError] = useState('');

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.filter = filter;
      if (selectedCourse !== 'all') params.courseId = selectedCourse;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const res = await getInstructorReviewsApi(params);
      if (res && res.success) {
        setReviews(res.reviews || []);
        if (res.courses) setCourses(res.courses);
        if (res.stats) setStats(res.stats);
      }
    } catch (err) {
      console.warn("Error fetching instructor reviews:", err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, selectedCourse, searchTerm]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleOpenReply = (rev) => {
    setReplyingReview(rev);
    setReplyText(rev.instructorReply?.comment || '');
    setActionError('');
    setActionSuccess('');
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyingReview || !replyText.trim()) return;

    try {
      setSubmittingReply(true);
      setActionError('');
      const res = await replyToReviewApi(replyingReview._id, replyText.trim());
      if (res && res.success) {
        setActionSuccess('Response successfully posted to the student!');
        setTimeout(() => {
          setReplyingReview(null);
          setActionSuccess('');
          fetchReviews();
        }, 1200);
      } else {
        setActionError(res?.message || 'Failed to post reply.');
      }
    } catch (err) {
      setActionError(err.response?.data?.message || 'Error submitting response.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this student review?')) return;
    try {
      const res = await deleteReviewApi(reviewId);
      if (res && res.success) {
        fetchReviews();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting review.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Instructor Rating KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500">Instructor Rating</span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-sm">
              <i className="ri-star-fill" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {stats.averageRating ? stats.averageRating.toFixed(1) : '4.8'} <span className="text-xs font-normal text-slate-400">/ 5.0</span>
          </p>
          <p className="text-[11px] text-amber-600 font-medium mt-1">Based on student reviews</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500">Total Reviews</span>
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center text-sm">
              <i className="ri-chat-smile-2-line" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-slate-900 tracking-tight">{stats.totalReviews}</p>
          <p className="text-[11px] text-teal-600 font-medium mt-1">Across all published courses</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500">Needs Response</span>
            <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-sm">
              <i className="ri-reply-line" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-rose-600 tracking-tight">{stats.unrepliedCount}</p>
          <p className="text-[11px] text-rose-500 font-medium mt-1">Awaiting instructor reply</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500">5-Star Ratio</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-sm">
              <i className="ri-award-line" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-emerald-600 tracking-tight">{stats.fiveStarRatio}%</p>
          <p className="text-[11px] text-emerald-600 font-medium mt-1">Learner satisfaction rate</p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-100 flex flex-wrap items-center justify-between gap-4 shadow-xs">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { id: 'all', label: 'All Reviews' },
            { id: 'unreplied', label: `Pending Reply (${stats.unrepliedCount})` },
            { id: 'replied', label: `Answered (${stats.repliedCount})` },
            { id: '5star', label: '⭐ 5 Stars' },
            { id: 'critical', label: '⚠️ Critical (≤3★)' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setFilter(item.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                filter === item.id
                  ? 'bg-teal-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {courses.length > 0 && (
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All My Courses</option>
              {courses.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.title}
                </option>
              ))}
            </select>
          )}

          <div className="relative flex-1 sm:w-56">
            <i className="ri-search-line absolute left-3 top-2 text-slate-400 text-xs" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search feedback or student..."
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
        </div>
      </div>

      {/* Reviews Roster */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
          <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-slate-500">Loading student reviews...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
          <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-teal-600 text-2xl">
            <i className="ri-chat-smile-3-line" />
          </div>
          <h3 className="font-bold text-slate-900 text-sm mb-1">No reviews found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {filter !== 'all' || searchTerm
              ? 'No student feedback matches your current filter query.'
              : 'As enrolled students complete your lessons and checkpoints, their reviews will appear here for you to respond!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((rev) => {
            const hasReply = Boolean(rev.instructorReply && rev.instructorReply.comment);
            const formattedDate = rev.createdAt
              ? new Date(rev.createdAt).toLocaleDateString('en-NG', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : 'Recent';

            return (
              <div
                key={rev._id}
                className="bg-white rounded-2xl border border-slate-100 p-6 shadow-xs hover:border-slate-200 transition-all space-y-4"
              >
                {/* Header info */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {rev.studentAvatar ? (
                      <img
                        src={rev.studentAvatar}
                        alt={rev.studentName}
                        className="w-10 h-10 rounded-full object-cover border border-slate-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                        {(rev.studentName || 'S').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-sm text-slate-900">{rev.studentName}</h4>
                        <span className="text-[10px] text-slate-400">({rev.studentEmail || 'Student'})</span>
                        {hasReply ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            ✓ Responded
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            ● Needs Reply
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <div className="flex items-center text-amber-400">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <i
                              key={s}
                              className={`ri-star-fill text-xs ${
                                s <= rev.rating ? 'text-amber-400' : 'text-slate-200'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-500 font-medium">
                          Course: <strong className="text-slate-700">{rev.courseId?.title || 'Enrolled Course'}</strong>
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-400">{formattedDate}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleOpenReply(rev)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
                    >
                      <i className="ri-reply-line" />
                      {hasReply ? 'Edit My Reply' : 'Respond to Student'}
                    </button>
                    <button
                      onClick={() => handleDelete(rev._id)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                      title="Delete review"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>

                {/* Review Text */}
                <div className="bg-slate-50/70 rounded-xl p-3.5 border border-slate-100 text-xs sm:text-sm text-slate-700">
                  {rev.title && <p className="font-bold text-slate-900 mb-1">{rev.title}</p>}
                  <p className="leading-relaxed whitespace-pre-line">{rev.comment}</p>
                </div>

                {/* Instructor Reply Box */}
                {hasReply && (
                  <div className="bg-teal-50/70 border border-teal-200/80 rounded-xl p-4 ml-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center text-[10px] font-bold">
                          <i className="ri-check-line" />
                        </div>
                        <span className="text-xs font-bold text-teal-900">
                          {rev.instructorReply.instructorName || 'Your Official Response'}
                        </span>
                        <span className="text-[10px] uppercase font-bold bg-teal-600 text-white px-1.5 py-0.2 rounded">
                          Instructor
                        </span>
                      </div>
                      <span className="text-[11px] text-teal-700/80">
                        {rev.instructorReply.repliedAt
                          ? new Date(rev.instructorReply.repliedAt).toLocaleDateString('en-NG', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : ''}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 leading-relaxed pl-7 whitespace-pre-line">
                      {rev.instructorReply.comment}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reply Modal / Overlay */}
      {replyingReview && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-base text-slate-900">
                  {replyingReview.instructorReply?.comment ? 'Edit Your Response' : 'Reply to Student Feedback'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Student: <strong className="text-slate-700">{replyingReview.studentName}</strong> • Course:{' '}
                  <strong className="text-teal-700">{replyingReview.courseId?.title}</strong>
                </p>
              </div>
              <button
                onClick={() => setReplyingReview(null)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Student's original review quote */}
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 mb-4 text-xs text-slate-600">
              <div className="flex items-center gap-1 text-amber-400 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i
                    key={s}
                    className={`ri-star-fill text-[11px] ${
                      s <= replyingReview.rating ? 'text-amber-400' : 'text-slate-200'
                    }`}
                  />
                ))}
              </div>
              <p className="italic font-medium text-slate-700">&ldquo;{replyingReview.comment}&rdquo;</p>
            </div>

            {actionError && (
              <div className="p-3 mb-3 bg-rose-50 border border-rose-200 text-xs text-rose-700 rounded-xl flex items-center gap-2">
                <i className="ri-error-warning-line text-sm" />
                {actionError}
              </div>
            )}

            {actionSuccess && (
              <div className="p-3 mb-3 bg-emerald-50 border border-emerald-200 text-xs text-emerald-700 rounded-xl flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-sm" />
                {actionSuccess}
              </div>
            )}

            <form onSubmit={handleSendReply} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Your Response (Publicly visible under student's review)
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Thank the student for their feedback, address questions, or provide encouragement..."
                  rows={5}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none leading-relaxed"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setReplyingReview(null)}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReply}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-md cursor-pointer"
                >
                  {submittingReply ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" />
                      Publish Response
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
